/**
 * Promove reboques embutidos (legado) para documentos vehicle com trailers: true,
 * vinculando-os via trailerIds e removendo do array reboques[] do trator.
 */

import vehicleService from "../../service/vehicle.service";
import { isReboqueStarted } from "../../view/vehicle/validation";
import { buildVehicleListParams } from "./vehicle-list-utils";
import {
  mapEmbeddedReboquesToApiPayload,
  normalizeTrailerIds,
  reboqueFormToTrailerCreatePayload,
  unwrapVehicleResponse,
} from "./reboque-trailer-utils";

const inFlightByTractorId = new Map();

export function normalizePlate(placa) {
  return String(placa ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
}

function unwrapList(response) {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractCreatedId(response, payload) {
  const created = unwrapVehicleResponse(response);
  if (created?.id) return String(created.id).trim();
  const body = response?.data;
  if (body?.id) return String(body.id).trim();
  if (body?.data?.id) return String(body.data.id).trim();
  if (payload?.id) return String(payload.id).trim();
  return "";
}

function buildTrailerSearchParams(user, uidShipper, plate) {
  const normalizedPlate = normalizePlate(plate);
  const base = user
    ? buildVehicleListParams(user, {
        vehiclePlate: normalizedPlate,
        vehicleType: "",
        renavam: "",
      })
    : null;

  if (base) {
    return {
      ...base,
      trailersFilter: "trailers",
      vehiclePlate: normalizedPlate,
    };
  }

  return {
    page: 1,
    limit: 100,
    trailersFilter: "trailers",
    vehiclePlate: normalizedPlate,
    ...(uidShipper ? { uidShipper } : { viewAll: "true" }),
  };
}

function pickTrailerByPlate(docs, plate) {
  const normalized = normalizePlate(plate);
  if (!normalized) return null;
  return (
    (docs || []).find(
      (doc) =>
        doc &&
        doc.trailers === true &&
        normalizePlate(doc.vehiclePlate) === normalized,
    ) || null
  );
}

/**
 * Busca reboque (trailers:true) existente pela placa no escopo do embarcador.
 */
export async function findExistingTrailerByPlate(plate, { user, uidShipper } = {}) {
  const normalized = normalizePlate(plate);
  if (!normalized) return null;

  const params = buildTrailerSearchParams(user, uidShipper, normalized);
  const response = await vehicleService.getAll(params);
  return pickTrailerByPlate(unwrapList(response), normalized);
}

/**
 * Em conflito de create, tenta achar trailer existente pela mesma placa.
 */
async function resolveTrailerAfterCreateConflict(plate, options) {
  try {
    return await findExistingTrailerByPlate(plate, options);
  } catch {
    return null;
  }
}

function getPromoteCandidates(reboques) {
  return (Array.isArray(reboques) ? reboques : []).filter(
    (reboque) =>
      isReboqueStarted(reboque) && Boolean(normalizePlate(reboque?.placaReboque)),
  );
}

async function doPromoteEmbeddedReboquesOnTractor(tractor, options = {}) {
  const { user = null } = options;
  const tractorId = String(tractor?.id || "").trim();
  const uidShipper =
    String(options.uidShipper || tractor?.uidShipper || "").trim();

  const emptyResult = {
    vehicle: tractor,
    promotedIds: [],
    errors: [],
    changed: false,
  };

  if (!tractorId || tractorId === "undefined" || tractor?.trailers === true) {
    return emptyResult;
  }

  const embedded = Array.isArray(tractor.reboques) ? tractor.reboques : [];
  const candidates = getPromoteCandidates(embedded);
  if (candidates.length === 0) {
    return emptyResult;
  }

  const existingTrailerIds = normalizeTrailerIds(tractor.trailerIds);
  const promotedIds = [];
  const promotedPlates = new Set();
  const errors = [];

  for (const reboque of candidates) {
    const plate = normalizePlate(reboque.placaReboque);
    if (promotedPlates.has(plate)) {
      continue;
    }

    try {
      let trailerId = "";
      const existing = await findExistingTrailerByPlate(plate, {
        user,
        uidShipper,
      });

      if (existing?.id) {
        trailerId = String(existing.id).trim();
      } else {
        const payload = reboqueFormToTrailerCreatePayload(reboque, uidShipper);
        try {
          const response = await vehicleService.create(payload);
          trailerId = extractCreatedId(response, payload);
          if (!trailerId) {
            throw new Error("Create de reboque não retornou id.");
          }
        } catch (createError) {
          const recovered = await resolveTrailerAfterCreateConflict(plate, {
            user,
            uidShipper,
          });
          if (recovered?.id) {
            trailerId = String(recovered.id).trim();
          } else {
            throw createError;
          }
        }
      }

      if (!trailerId) {
        throw new Error(`Não foi possível obter id do reboque ${plate}.`);
      }

      promotedIds.push(trailerId);
      promotedPlates.add(plate);
    } catch (error) {
      errors.push({
        plate,
        message:
          error?.response?.data?.message ||
          error?.message ||
          `Falha ao promover reboque ${plate}.`,
      });
    }
  }

  if (promotedIds.length === 0) {
    return {
      vehicle: tractor,
      promotedIds: [],
      errors,
      changed: false,
    };
  }

  const nextTrailerIds = normalizeTrailerIds([
    ...existingTrailerIds,
    ...promotedIds,
  ]);

  const remainingEmbedded = embedded.filter((reboque) => {
    const plate = normalizePlate(reboque?.placaReboque);
    if (!plate) return true;
    return !promotedPlates.has(plate);
  });

  await vehicleService.update(tractorId, {
    trailerIds: nextTrailerIds,
    reboques: mapEmbeddedReboquesToApiPayload(
      remainingEmbedded,
      isReboqueStarted,
    ),
  });

  const nextVehicle = {
    ...tractor,
    id: tractorId,
    trailerIds: nextTrailerIds,
    reboques: remainingEmbedded,
  };

  return {
    vehicle: nextVehicle,
    promotedIds: normalizeTrailerIds(promotedIds),
    errors,
    changed: true,
  };
}

/**
 * Promove reboques legados do trator para docs trailers:true + trailerIds.
 * Idempotente por tractorId (chamadas concorrentes compartilham a mesma Promise).
 *
 * @returns {Promise<{ vehicle: object, promotedIds: string[], errors: Array<{plate:string,message:string}>, changed: boolean }>}
 */
export function promoteEmbeddedReboquesOnTractor(tractor, options = {}) {
  const tractorId = String(tractor?.id || "").trim();
  if (!tractorId || tractorId === "undefined") {
    return Promise.resolve({
      vehicle: tractor,
      promotedIds: [],
      errors: [],
      changed: false,
    });
  }

  if (inFlightByTractorId.has(tractorId)) {
    return inFlightByTractorId.get(tractorId);
  }

  const promise = doPromoteEmbeddedReboquesOnTractor(tractor, options).finally(
    () => {
      inFlightByTractorId.delete(tractorId);
    },
  );
  inFlightByTractorId.set(tractorId, promise);
  return promise;
}

/** Exposto só para testes. */
export function __resetPromoteInFlightForTests() {
  inFlightByTractorId.clear();
}
